import { useCallback, useEffect, useState } from 'react';

import { CurrentAccess, PersonneMoralePlateforme, getMyAccess } from '../api/getMyAccess';

export type AccessStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseCurrentAccessResult {
  access: CurrentAccess | null;
  plateformes: PersonneMoralePlateforme[];
  status: AccessStatus;
  isReady: boolean;
  isMember: boolean;
  canManageTeam: boolean;
  canEdit: boolean;
  canRead: boolean;
  refresh: () => Promise<void>;
}

/**
 * Hook qui résout l'accès du user courant à sa personne morale.
 * Comme un user appartient à au plus 1 PM, pas besoin de switcher.
 *
 * À terme : hydrater depuis le store Redux pour éviter un fetch à chaque mount.
 */
export function useCurrentAccess(): UseCurrentAccessResult {
  const [access, setAccess] = useState<CurrentAccess | null>(null);
  const [plateformes, setPlateformes] = useState<PersonneMoralePlateforme[]>([]);
  const [status, setStatus] = useState<AccessStatus>('idle');

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      const response = await getMyAccess();
      setAccess(response.access);
      setPlateformes(response.plateformes);
      setStatus('ready');
    } catch (e) {
      console.error('Failed to fetch current access', e);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    access,
    plateformes,
    status,
    isReady: status === 'ready',
    isMember: !!access,
    canManageTeam: access?.isOwner === true,
    canEdit: !!access,
    canRead: !!access,
    refresh,
  };
}
